@let status = consentApiStatus();
@let group = consentGroup();

<ng-template #form>
  @switch (status) {
    @case (ConsentApiStatus.NOT_LOADED) {
      <fcom-loader />
    }
    @case (ConsentApiStatus.LOADED) {
      @if (group) {
        @if (!useModal()) {
          <h1 class="font-heading-2 font-headline-medium">{{ 'marketingConsent.manageTitle' | finLocalization }}</h1>
        }

        @if (enableConsentChanges) {
          <form
            [formGroup]="marketingPreferencesForm"
            (ngSubmit)="sendConsents()"
            class="marketing-preferences pr-small-t text"
            [ngClass]="{
              'pr-large-x': useModal(),
            }"
          >
            <fieldset>
              <legend class="align-left">
                <div class="font-body-1 font-body-medium">
                  <p class="pr-small-b ps-xxsmall-t">
                    {{ 'plus.memberMarketingPreferences.memberNumber' | finLocalization }}:
                    <strong>{{ group.fplusMember }}</strong>
                  </p>
                </div>
              </legend>

              <fcom-heading-tag [level]="useModal() ? 4 : 2" [extraClasses]="'font-heading-4 font-title-medium'">{{
                'marketingConsent.automaticMessagesTitle' | finLocalization
              }}</fcom-heading-tag>
              <p class="font-body-1 font-body-medium italic ms-xsmall-t">
                {{ 'marketingConsent.automaticMessagesDescription' | finLocalization }}
              </p>
              <ul class="ms-medium-l font-body-1 font-body-medium">
                <li
                  class="ms-small-t"
                  [innerHTML]="'marketingConsent.flightDisruptions' | finLocalization | finSafe: 'html'"
                ></li>
                <li
                  class="ms-small-t"
                  [innerHTML]="'marketingConsent.bookingConfirmations' | finLocalization | finSafe: 'html'"
                ></li>
              </ul>

              @for (consentLayoutGroup of consentLayoutGroups(); track consentLayoutGroup.titleLabel) {
                <fcom-heading-tag
                  [level]="useModal() ? 4 : 2"
                  [extraClasses]="'font-heading-4 font-title-medium ms-xxlarge-t'"
                  >{{ consentLayoutGroup.titleLabel | finLocalization }}
                </fcom-heading-tag>

                @if (consentLayoutGroup.descriptionLabel) {
                  <p
                    class="italic ms-xsmall-y"
                    [innerHTML]="consentLayoutGroup.descriptionLabel | finLocalization | finSafe: 'html'"
                  ></p>
                }

                @if (consentLayoutGroup.consents?.length > 0) {
                  @for (consent of consentLayoutGroup.consents; track consent.consentTextId; let last = $last) {
                    <div class="ps-small-y">
                      <fcom-checkbox-input
                        [controlName]="consent.consentTextId"
                        [parentForm]="marketingPreferencesForm"
                      >
                        <span class="content" [innerHTML]="consent.text | finSafe: 'html'"></span>
                      </fcom-checkbox-input>
                    </div>
                  }
                }

                @if (consentLayoutGroup.consentLayoutGroups) {
                  @for (
                    nestedConsentLayoutGroup of consentLayoutGroup.consentLayoutGroups;
                    track nestedConsentLayoutGroup.titleLabel
                  ) {
                    <fcom-heading-tag
                      [level]="useModal() ? 5 : 3"
                      [extraClasses]="'ms-large-t font-body-1 font-body-medium'"
                      >{{ nestedConsentLayoutGroup.titleLabel | finLocalization }}
                    </fcom-heading-tag>

                    @if (nestedConsentLayoutGroup.descriptionLabel) {
                      <p
                        class="ms-xsmall-t"
                        [innerHTML]="nestedConsentLayoutGroup.descriptionLabel | finLocalization | finSafe: 'html'"
                      ></p>
                    }

                    @if (nestedConsentLayoutGroup.consents?.length > 0) {
                      @for (
                        consent of nestedConsentLayoutGroup.consents;
                        track consent.consentTextId;
                        let last = $last
                      ) {
                        <div
                          [ngClass]="{
                            'ps-small-y': consent.consentTextId !== ConsentTextId.BOOKING_COMMUNICATION,
                            'ps-small-t': consent.consentTextId === ConsentTextId.BOOKING_COMMUNICATION,
                          }"
                        >
                          <fcom-checkbox-input
                            [controlName]="consent.consentTextId"
                            [parentForm]="marketingPreferencesForm"
                          >
                            <span class="content" [innerHTML]="consent.text | finSafe: 'html'"></span>
                          </fcom-checkbox-input>
                        </div>
                        @if (consent.consentTextId === ConsentTextId.BOOKING_COMMUNICATION && !useModal()) {
                          <button
                            type="button"
                            class="font-body-1 font-body-medium bold-type underline ms-small-b ms-xlarge-l ms-xxsmall-t"
                            (click)="infoModalOpen.set(true)"
                          >
                            {{ 'marketingConsent.bookingCommunicationModalLink' | finLocalization }}
                          </button>
                          <fcom-modal [(open)]="infoModalOpen" [buttons]="ModalButtons.CLOSE">
                            <div class="pr-small">
                              <h4 class="mr-small-b">
                                {{ 'marketingConsent.bookingCommunicationModalTitle' | finLocalization }}
                              </h4>
                              <cms-unstyled-fragment
                                [contentUrl$]="
                                  'fragments.bookingCommunicationModalContent.url' | finLocalization | finToObservable
                                "
                                class="block"
                              />
                            </div>
                          </fcom-modal>
                        }
                      }
                    }
                  }
                }
              }
            </fieldset>
            <p
              class="ms-large-t ms-large-b align-left"
              [innerHTML]="'plus.memberMarketingPreferences.disclaimer' | finLocalization | finSafe: 'html'"
            ></p>
          </form>
        } @else {
          <form
            [formGroup]="marketingPreferencesForm"
            (ngSubmit)="sendConsents()"
            class="marketing-preferences pr-large-x pr-medium-y text"
          >
            <fieldset>
              <legend class="align-left">
                <div class="font-body-1 font-body-medium">
                  <p class="pr-small-b ps-xxsmall-t">
                    {{ 'plus.memberMarketingPreferences.memberNumber' | finLocalization }}:
                    <strong>{{ group.fplusMember }}</strong>
                  </p>
                  <p
                    data-testid="cms-description"
                    [innerHTML]="'plus.memberMarketingPreferences.description' | finLocalization | finSafe: 'html'"
                  ></p>
                </div>
              </legend>

              @for (consent of group.consents; track consent.consentTextId; let last = $last) {
                <fcom-checkbox-input
                  [controlName]="consent.consentTextId"
                  [parentForm]="marketingPreferencesForm"
                  [ngClass]="{
                    block: true,
                    'ms-small-t': true,
                    'ms-small-b': last,
                  }"
                >
                  <span class="content" [innerHTML]="consent.text | finSafe: 'html'"></span>
                </fcom-checkbox-input>
              }
            </fieldset>
            <hr />
            <p
              class="pr-small-t align-left"
              [innerHTML]="'plus.memberMarketingPreferences.disclaimer' | finLocalization | finSafe: 'html'"
            ></p>
          </form>
        }
      }
    }
    @case (ConsentApiStatus.SAVED) {
      @if (!useModal()) {
        <fcom-icon class="icon-xlarge mr-small-y" [name]="SvgLibraryIcon.CHECKMARK_BLOCK" />
        <fcom-heading-tag [level]="2" [extraClasses]="'font-heading-2 font-headline-medium mr-xsmall-b'">
          {{ 'plus.memberMarketingPreferences.successTitle' | finLocalization }}
        </fcom-heading-tag>
        <p
          class="mr-medium-b"
          [innerHTML]="'plus.memberMarketingPreferences.success' | finLocalization | finSafe: 'html'"
        ></p>
      } @else {
        <p
          class="pr-large-x pr-small-y"
          [innerHTML]="'plus.memberMarketingPreferences.success' | finLocalization | finSafe: 'html'"
        ></p>
      }
    }
    @case (ConsentApiStatus.ERROR) {
      <p
        class="pr-large-x pr-small-y"
        [innerHTML]="'plus.memberMarketingPreferences.error' | finLocalization | finSafe: 'html'"
      ></p>
    }
  }
</ng-template>

<ng-template #buttons>
  @switch (status) {
    @case (ConsentApiStatus.LOADED) {
      <fcom-button
        [ngClass]="useModal() ? 'pr-xxsmall-l' : null"
        [theme]="useModal() ? ButtonTheme.LIGHT_SECONDARY_OUTLINE : ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE"
        [mode]="useModal() ? ButtonMode.INLINE : ButtonMode.FILL_MOBILE"
        [text]="'plus.memberMarketingPreferences.saveButtonTitle' | finLocalization"
        [disabled]="!marketingPreferencesForm?.valid"
        data-testid="save-button"
        (clickBtn)="sendConsents()"
      />
    }
    @case (ConsentApiStatus.SAVED) {
      @let url = successButtonUrl();
      @if (url) {
        <fcom-button
          [theme]="ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE"
          [text]="'plus.memberMarketingPreferences.successButtonTitle' | finLocalization"
          data-testid="success-button"
          (clickBtn)="onClick(url)"
        />
      }
    }
    @case (ConsentApiStatus.ERROR) {
      @let url = errorButtonUrl();
      @if (url) {
        <fcom-button
          [theme]="ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE"
          [text]="'plus.memberMarketingPreferences.errorButtonTitle' | finLocalization"
          data-testid="error-button"
          (clickBtn)="onClick(url)"
        />
      }
    }
  }
</ng-template>

@if (useModal()) {
  <fcom-modal
    [open]="modalOpen()"
    [buttons]="ModalButtons.CUSTOM"
    [fitToContent]="true"
    [title]="
      status === ConsentApiStatus.NOT_LOADED || status === ConsentApiStatus.LOADED
        ? ('plus.memberMarketingPreferences.title' | finLocalization)
        : status === ConsentApiStatus.SAVED
          ? ('plus.memberMarketingPreferences.successTitle' | finLocalization)
          : ('plus.memberMarketingPreferences.errorTitle' | finLocalization)
    "
    [forceRemainOpen]="modalOpen()"
  >
    <ng-container *ngTemplateOutlet="form"></ng-container>
    <div buttons class="flex flex--center flex--middle">
      <ng-container *ngTemplateOutlet="buttons"></ng-container>
    </div>
  </fcom-modal>
} @else {
  <ng-container *ngTemplateOutlet="form"></ng-container>
  <div>
    <ng-container *ngTemplateOutlet="buttons"></ng-container>
  </div>
}
